import { createRelation, deleteRelation, getRelations, updateRelation } from '@entities/relation/api'
import type { PaginatedList } from '@shared/api/types'
import { getErrorMessage } from '@shared/lib/errors'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useState } from 'react'
import type { Relation } from '../types'

export const useRelations = (role?: string) => {
    const queryClient = useQueryClient()

    const [pagination, setPagination] = useState({ page: 1, pageSize: 10 })

    const queryKey = ['relations', pagination.page, pagination.pageSize, role]

    const { message } = App.useApp()

    const relations = useQuery({
        queryKey,
        queryFn: () => getRelations({ page: pagination.page, pageSize: pagination.pageSize, role }),
        placeholderData: keepPreviousData,
        enabled: !!role
    })

    const createRelationMutation = useMutation({
        mutationFn: createRelation,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Relation>>({
                queryKey: ['relations'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })
            message.success('Связка успешно создана')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const editRelationMutation = useMutation({
        mutationFn: updateRelation,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Relation>>({
                queryKey: ['relations'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })
            message.success('Связка успешно обновлена')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const deleteRelationMutation = useMutation({
        mutationFn: deleteRelation,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Relation>>({
                queryKey: ['relations'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })
            message.success('Связка успешно удалена')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    return {
        relations: relations.data,
        pagination,
        isRelationsLoading: relations.isPlaceholderData,
        actions: {
            setPagination,
            createRelation: createRelationMutation.mutateAsync,
            editRelation: editRelationMutation.mutateAsync,
            deleteRelation: deleteRelationMutation.mutateAsync
        }
    }
}
